import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Public as PublicIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
  Language as LanguageIcon,
  Handshake as HandshakeIcon,
  LocalShipping as CargoIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ATSInternationalSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/ats-system');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to ATS System"
        >
          Back to ATS
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            ATS-International (ATS-I)
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            International Customs Collaboration Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Subsystem Overview
          </Typography>
          <Typography variant="body1" paragraph>
            ATS-International (ATS-I) facilitates collaboration between U.S. Customs and Border Protection and foreign 
            customs authorities on high-risk cargo targeting and information sharing. Through bilateral and multilateral 
            agreements, ATS-I enables partner nations to share targeting intelligence, coordinate cargo examinations, and 
            jointly identify threats before shipments reach U.S. borders, creating a layered defense approach to supply 
            chain security and international trade protection.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Subsystem Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="ATS-International 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is ATS-International"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    An <strong>international cargo targeting collaboration platform</strong> that enables <strong>information 
                    sharing between CBP and foreign customs agencies</strong>. Facilitates <strong>joint targeting of high-risk 
                    cargo, coordinated examinations, and intelligence exchange</strong> on shipments destined for the U.S. through 
                    <strong> bilateral agreements, Container Security Initiative (CSI), and Customs-Trade Partnership Against 
                    Terrorism (C-TPAT)</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses ATS-International"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Used by <strong>CBP National Targeting Center (NTC) analysts</strong>, <strong>CBP attachés stationed 
                    at foreign ports</strong>, <strong>foreign customs authorities</strong> in partner countries, and 
                    <strong> international trade security organizations</strong>. Supports <strong>World Customs Organization 
                    (WCO)</strong> and <strong>bilateral customs agreements</strong> with 80+ countries.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was ATS-International deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initiated in <strong>2002</strong> with Container Security Initiative (CSI) following 9/11. Expanded 
                    through <strong>2004-2008</strong> with bilateral agreements. Major enhancements in <strong>2010-2015</strong> 
                    with automated data exchange and <strong>2018-2020</strong> with secure information sharing platforms. 
                    Operates <strong>24/7</strong> with real-time coordination across time zones.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is ATS-International deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>CBP National Targeting Center (NTC)</strong> coordinates operations. <strong>CBP officers stationed 
                    at 60+ foreign seaports</strong> including Rotterdam, Hong Kong, Singapore, Dubai, and Shanghai. Partner 
                    customs agencies in <strong>80+ countries</strong> across Europe, Asia, Middle East, and Americas participate 
                    in information sharing and joint targeting.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why ATS-International exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To <strong>push U.S. borders outward</strong> by identifying threats at foreign ports before cargo is 
                    loaded onto U.S.-bound vessels. Enables <strong>joint examinations, intelligence sharing, and coordinated 
                    enforcement</strong> with partner nations. Critical for <strong>supply chain security, counterterrorism, 
                    and trade protection</strong>. Screens cargo at <strong>source countries</strong>, reducing risk of WMD, 
                    contraband, and threats reaching U.S. shores.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SecurityIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        International Collaboration Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <HandshakeIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Information Sharing
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Secure exchange of targeting intelligence with partner nations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Cargo Data Exchange:</strong> Manifest and shipment information sharing</Typography></li>
                <li><Typography variant="body2"><strong>Targeting Intelligence:</strong> High-risk entity and commodity alerts</Typography></li>
                <li><Typography variant="body2"><strong>Seizure Information:</strong> Past enforcement actions and patterns</Typography></li>
                <li><Typography variant="body2"><strong>Threat Assessments:</strong> Joint analysis of emerging risks</Typography></li>
                <li><Typography variant="body2"><strong>Best Practices:</strong> Targeting methodologies and techniques</Typography></li>
                <li><Typography variant="body2"><strong>Secure Communications:</strong> Encrypted data transmission protocols</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CargoIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Joint Cargo Targeting
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Coordinated targeting of high-risk shipments at foreign ports.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Pre-Loading Screening:</strong> Risk assessment before vessel departure</Typography></li>
                <li><Typography variant="body2"><strong>Coordinated Exams:</strong> Joint inspections with foreign customs</Typography></li>
                <li><Typography variant="body2"><strong>NII Scanning:</strong> X-ray and gamma ray imaging at foreign ports</Typography></li>
                <li><Typography variant="body2"><strong>Physical Inspections:</strong> Container examinations before loading</Typography></li>
                <li><Typography variant="body2"><strong>Hold Authority:</strong> Prevent loading of high-risk cargo</Typography></li>
                <li><Typography variant="body2"><strong>Exam Results:</strong> Findings shared between agencies</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PublicIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Container Security Initiative (CSI)
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                CBP officers stationed at foreign seaports for advance screening.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>60+ CSI Ports:</strong> Major seaports in Europe, Asia, Middle East</Typography></li>
                <li><Typography variant="body2"><strong>CBP Presence:</strong> Officers embedded with foreign customs</Typography></li>
                <li><Typography variant="body2"><strong>Pre-Departure Targeting:</strong> U.S.-bound cargo screening</Typography></li>
                <li><Typography variant="body2"><strong>Bilateral Agreements:</strong> Formal partnerships with host countries</Typography></li>
                <li><Typography variant="body2"><strong>Technology Deployment:</strong> NII equipment at foreign ports</Typography></li>
                <li><Typography variant="body2"><strong>Training Programs:</strong> Capacity building for partner customs</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LanguageIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Multilateral Cooperation
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Engagement with international trade security organizations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>World Customs Organization:</strong> SAFE Framework implementation</Typography></li>
                <li><Typography variant="body2"><strong>C-TPAT:</strong> Customs-Trade Partnership Against Terrorism</Typography></li>
                <li><Typography variant="body2"><strong>Mutual Recognition:</strong> Trusted trader program alignment</Typography></li>
                <li><Typography variant="body2"><strong>Regional Initiatives:</strong> APEC, EU, ASEAN cooperation</Typography></li>
                <li><Typography variant="body2"><strong>Standards Development:</strong> International targeting standards</Typography></li>
                <li><Typography variant="body2"><strong>Joint Operations:</strong> Coordinated enforcement campaigns</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* CSI Ports */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Major CSI Port Locations
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Region</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Major CSI Ports</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Annual Volume to U.S.</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>Europe</strong></TableCell>
              <TableCell>Rotterdam, Hamburg, Antwerp, Felixstowe, Le Havre</TableCell>
              <TableCell>~2 million TEUs</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>East Asia</strong></TableCell>
              <TableCell>Shanghai, Hong Kong, Shenzhen, Busan, Yokohama</TableCell>
              <TableCell>~8 million TEUs</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Southeast Asia</strong></TableCell>
              <TableCell>Singapore, Port Klang, Laem Chabang, Jakarta</TableCell>
              <TableCell>~1.5 million TEUs</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Middle East</strong></TableCell>
              <TableCell>Dubai, Jebel Ali, Haifa, Ashdod</TableCell>
              <TableCell>~500K TEUs</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Americas</strong></TableCell>
              <TableCell>Vancouver, Montreal, Santos, Buenos Aires</TableCell>
              <TableCell>~1 million TEUs</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>Africa</strong></TableCell>
              <TableCell>Durban, Cape Town, Port Said</TableCell>
              <TableCell>~200K TEUs</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Program Scale & Impact
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                80+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Partner Countries
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Bilateral agreements
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                60+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                CSI Ports
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Foreign port locations
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                80%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                U.S. Cargo Covered
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Containerized imports
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                24/7
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Operations
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Global coordination
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Strategic Value
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Extended Border:</strong> Push security screening to foreign ports</Typography></li>
                <li><Typography variant="body2"><strong>Pre-Loading Interdiction:</strong> Stop threats before vessel departure</Typography></li>
                <li><Typography variant="body2"><strong>Intelligence Sharing:</strong> Leverage partner nation insights</Typography></li>
                <li><Typography variant="body2"><strong>Joint Operations:</strong> Coordinated enforcement actions</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Supply Chain Security:</strong> Protect global trade networks</Typography></li>
                <li><Typography variant="body2"><strong>WMD Prevention:</strong> Screen for weapons of mass destruction</Typography></li>
                <li><Typography variant="body2"><strong>Trade Facilitation:</strong> Expedite low-risk cargo clearance</Typography></li>
                <li><Typography variant="body2"><strong>Capacity Building:</strong> Strengthen partner customs capabilities</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Partner Programs */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            International Partnership Programs
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Chip label="Container Security Initiative (CSI)" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Pre-screening at 60+ foreign ports</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="C-TPAT" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Customs-Trade Partnership</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Mutual Recognition Agreements" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Trusted trader alignment</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="WCO SAFE Framework" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Global supply chain standards</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Bilateral Agreements" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Country-specific partnerships</Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Chip label="Regional Initiatives" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">APEC, EU, ASEAN cooperation</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default ATSInternationalSystemDetail;
